import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import BlogCard from '../../components/blog-card/BlogCard';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { Blog } from '../../types/blogTypes';

type BlogCategory = 'tenant' | 'landlord';
type PaginatedState = {
  items: Blog[];
  cursor: string;
  hasMore: boolean;
  loading: boolean;
  initialized: boolean;
};

const PAGE_SIZE = 12;
const BlogCardSkeleton: React.FC = () => (
  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm animate-pulse">
    <div className="h-44 w-full rounded-2xl bg-slate-200" />
    <div className="mt-4 h-5 w-3/4 rounded bg-slate-200" />
    <div className="mt-3 h-4 w-full rounded bg-slate-200" />
    <div className="mt-2 h-4 w-5/6 rounded bg-slate-200" />
    <div className="mt-5 h-10 w-32 rounded-xl bg-slate-200" />
  </div>
);

const normalizeRemoteBlog = (item: Partial<Blog> & { slug?: string }) => {
  const id = String(item?.id || item?.slug || '').trim().toLowerCase();
  if (!id) return null;
  return {
    id,
    slug: id,
    title: String(item?.title || '').trim(),
    excerpt: String(item?.excerpt || '').trim(),
    image: String(item?.image || '').trim(),
    content: String(item?.content || '').trim(),
    category: item?.category === 'landlord' ? 'landlord' : 'tenant',
    seoDescription: String(item?.seoDescription || '').trim(),
    seoKeywords: Array.isArray(item?.seoKeywords) ? item.seoKeywords : [],
    blog_schema: item?.blog_schema,
    is_firestore_blog: true,
    route_path: String(item?.route_path || '').trim() || `/blog/${id}`,
  } as Blog;
};

const Blogs: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [tenantState, setTenantState] = useState<PaginatedState>({
    items: [],
    cursor: '',
    hasMore: true,
    loading: false,
    initialized: false,
  });
  const [landlordState, setLandlordState] = useState<PaginatedState>({
    items: [],
    cursor: '',
    hasMore: true,
    loading: false,
    initialized: false,
  });

  const fetchCategoryPage = useCallback(
    async (category: BlogCategory, append = true) => {
      const state = category === 'tenant' ? tenantState : landlordState;
      const setState = category === 'tenant' ? setTenantState : setLandlordState;

      if (state.loading) return;
      if (append && !state.hasMore) return;

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const params = new URLSearchParams({
          category,
          limit: String(PAGE_SIZE),
        });
        if (append && state.cursor) {
          params.set('cursor', state.cursor);
        }

        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/getBlogPosts?${params.toString()}`);
        const result = await response.json();
        if (!result?.success || !Array.isArray(result?.data)) {
          setState((prev) => ({ ...prev, loading: false, initialized: true, hasMore: false }));
          return;
        }

        const pageItems = result.data.map(normalizeRemoteBlog).filter(Boolean) as Blog[];
        const nextCursor = String(result?.nextCursor || '').trim();

        setState((prev) => {
          const merged = append ? [...prev.items, ...pageItems] : pageItems;
          const seen = new Set<string>();
          const deduped = merged.filter((blog) => {
            if (!blog?.id || seen.has(blog.id)) return false;
            seen.add(blog.id);
            return true;
          });

          return {
            ...prev,
            items: deduped,
            cursor: nextCursor,
            hasMore: Boolean(nextCursor) && pageItems.length > 0,
            loading: false,
            initialized: true,
          };
        });
      } catch (error) {
        setState((prev) => ({ ...prev, loading: false, initialized: true, hasMore: false }));
      }
    },
    [tenantState, landlordState]
  );

  useEffect(() => {
    if (!tenantState.initialized && !tenantState.loading) {
      fetchCategoryPage('tenant', false);
    }
    if (!landlordState.initialized && !landlordState.loading) {
      fetchCategoryPage('landlord', false);
    }
  }, [
    fetchCategoryPage,
    tenantState.initialized,
    tenantState.loading,
    landlordState.initialized,
    landlordState.loading,
  ]);

  const getTabClassName = (index: number) => {
    const baseClasses =
      'px-6 py-2 text-sm font-medium rounded-full transition-all duration-200 ease-in-out focus:outline-none cursor-pointer';
    const selectedClasses = 'bg-blue-500 text-white border border-blue-500';
    const unselectedClasses =
      'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200';

    return `${baseClasses} ${selectedTab === index ? selectedClasses : unselectedClasses}`;
  };

  // SEO Helmet block
  const seoTitle =
    'RentSmart Insights Blog | Rental Advice for Tenants & Landlords';
  const seoDescription =
    'Read expert articles, tips, and guides for tenants and landlords in Ireland. Stay informed with RentSmart Insights Blog on renting, property management, and market trends.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="rental blog, property advice, tenant tips, landlord tips, Ireland, RentSmart, market trends, property management"
        />
        <link rel="canonical" href="https://rentsmart.ie/blog" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/blog" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          RentSmart Insights Blog
        </h1>
        {(tenantState.loading || landlordState.loading) && !tenantState.initialized && !landlordState.initialized ? (
          <div className="mb-8 rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500"></span>
              </span>
              <p className="text-sm font-semibold text-slate-700">
                Fetching latest blogs from RentSmart intelligence feed...
              </p>
            </div>
          </div>
        ) : null}
        <Tabs
          selectedIndex={selectedTab}
          onSelect={(index: number) => setSelectedTab(index)}
        >
          <TabList className="flex justify-center mb-8 space-x-2">
            <Tab className={getTabClassName(0)}>For Tenants</Tab>
            <Tab className={getTabClassName(1)}>For Landlords</Tab>
          </TabList>

          <TabPanel>
            {tenantState.loading && !tenantState.initialized ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array.from({ length: 6 }).map((_, index) => (
                  <BlogCardSkeleton key={`tenant-skeleton-${index}`} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {tenantState.items.map((blog) => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    toPath={`/blog/${blog.slug || blog.id}`}
                  />
                ))}
              </div>
            )}
            {tenantState.hasMore ? (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => fetchCategoryPage('tenant', true)}
                  disabled={tenantState.loading}
                  className="px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
                >
                  {tenantState.loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            ) : null}
          </TabPanel>

          <TabPanel>
            {landlordState.loading && !landlordState.initialized ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {Array.from({ length: 6 }).map((_, index) => (
                  <BlogCardSkeleton key={`landlord-skeleton-${index}`} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {landlordState.items.map((blog) => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    toPath={`/blog/${blog.slug || blog.id}`}
                  />
                ))}
              </div>
            )}
            {landlordState.hasMore ? (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => fetchCategoryPage('landlord', true)}
                  disabled={landlordState.loading}
                  className="px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
                >
                  {landlordState.loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            ) : null}
          </TabPanel>
        </Tabs>
      </div>
    </>
  );
};

export default Blogs;
